import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { tokens } from "@/art/tokens";
import { cn } from "@/utils/cn";

interface StatBarProps {
    label: string;
    value: number;
    max?: number;
    color?: string;
    icon?: string;
    /** 폭 고정을 깨야 할 때. 기본은 flex 자동. */
    width?: number | string;
    className?: string;
    /** 수치 표시 여부. */
    showNumber?: boolean;
}

/**
 * 범용 자원/스탯 게이지. HUD 의 체력·정신·컨디션, 시트의 스탯 막대 등 광범위 사용.
 * 값이 바뀌면 Framer Motion 이 부드럽게 보간.
 */
export function StatBar({
    label,
    value,
    max = 100,
    color = tokens.color.ember,
    icon,
    width,
    className,
    showNumber = true,
}: StatBarProps) {
    const previousValue = useRef(value);
    const [pulseTick, setPulseTick] = useState(0);
    // 2026-04-22: 값 변화 감지 시 "+N" 플로팅 칩을 1.4초간 노출 (우마무스메 A안).
    //   컷인 없이 조용히, 숫자만 스탯 바 위로 떠오르며 사라짐.
    const [recentDelta, setRecentDelta] = useState<number>(0);
    const pct = Math.max(0, Math.min(1, max > 0 ? value / max : 0));
    const roundedValue = Math.round(value);

    useEffect(() => {
        const prevRounded = Math.round(previousValue.current);
        if (prevRounded !== roundedValue) {
            const delta = roundedValue - prevRounded;
            setPulseTick(tick => tick + 1);
            previousValue.current = value;
            if (delta !== 0) {
                setRecentDelta(delta);
                const t = window.setTimeout(() => setRecentDelta(0), 1400);
                return () => window.clearTimeout(t);
            }
        }
    }, [roundedValue, value]);

    const deltaColor = recentDelta > 0 ? color : tokens.color.burgundy;

    return (
        <div className={cn("flex items-center gap-s", className)} style={{ width }}>
            {icon && <span className="text-body select-none">{icon}</span>}
            <div className="flex-1 flex flex-col gap-[2px]">
                <div className="flex items-center justify-between">
                    <span
                        className="font-body text-small"
                        style={{ color: tokens.color.parchmentDim, letterSpacing: tokens.type.letterSpacing.wide }}
                    >
                        {label}
                    </span>
                    {showNumber && (
                        <span className="relative inline-flex items-center">
                            <motion.span
                                key={`${label}-${pulseTick}`}
                                className="font-stat text-small"
                                initial={{ scale: 1, opacity: 0.92 }}
                                animate={{ scale: [1, 1.14, 1], opacity: [0.92, 1, 0.92] }}
                                transition={{ duration: 0.55, ease: tokens.easing.decelerate }}
                                style={{ color: tokens.color.parchment }}
                            >
                                {roundedValue}
                                <span style={{ color: tokens.color.parchmentDim }}> / {max}</span>
                            </motion.span>
                            {recentDelta !== 0 && (
                                <motion.span
                                    key={`delta-${pulseTick}`}
                                    initial={{ opacity: 0, y: 0 }}
                                    animate={{ opacity: [0, 1, 1, 0], y: [0, -8, -14, -20] }}
                                    transition={{ duration: 1.3, ease: tokens.easing.decelerate, times: [0, 0.18, 0.72, 1] }}
                                    className="absolute -right-[2px] -top-[2px] font-stat select-none pointer-events-none"
                                    style={{
                                        color: deltaColor,
                                        fontSize: tokens.type.size.tiny,
                                        textShadow: `0 0 6px ${deltaColor}99`,
                                    }}
                                >
                                    {recentDelta > 0 ? `+${recentDelta}` : recentDelta}
                                </motion.span>
                            )}
                        </span>
                    )}
                </div>
                {/* 2026-04-23: 게이지 외곽 glow — 저점일 때 위험 색으로 pulse. */}
                <motion.div
                    className="relative h-[7px] rounded-pill overflow-hidden"
                    animate={pct < 0.3 ? {
                        boxShadow: [
                            `0 0 0px #E0859400`,
                            `0 0 10px #E08594AA, 0 0 2px #E08594`,
                            `0 0 0px #E0859400`,
                        ],
                    } : { boxShadow: "none" }}
                    transition={{ duration: 1.2, repeat: pct < 0.3 ? Infinity : 0, ease: "easeInOut" }}
                    style={{
                        background: `${tokens.color.steel}44`,
                        border: pct < 0.3 ? `1px solid #E0859466` : `1px solid ${tokens.color.parchmentDim}14`,
                    }}
                >
                    <motion.div
                        key={`glow-${label}-${pulseTick}`}
                        className="absolute inset-[-2px] rounded-pill"
                        initial={{ opacity: 0, scaleX: 0.92 }}
                        animate={{ opacity: [0, 0.65, 0], scaleX: [0.92, 1, 1.02] }}
                        transition={{ duration: 0.72, ease: tokens.easing.decelerate }}
                        style={{
                            background: `linear-gradient(90deg, transparent 0%, ${color}66 42%, ${color}88 52%, transparent 100%)`,
                            transformOrigin: "left center",
                        }}
                    />
                    <motion.div
                        className="absolute inset-y-0 left-0 rounded-pill"
                        style={{
                            background: `linear-gradient(90deg, ${color}CC 0%, ${color} 60%, ${color}DD 100%)`,
                            boxShadow: `0 0 8px ${color}80, inset 0 1px 0 ${color}AA`,
                        }}
                        initial={{ width: 0 }}
                        animate={{ width: `${pct * 100}%` }}
                        transition={{ duration: 0.6, ease: tokens.easing.decelerate }}
                    />
                    {/* 진행 중 shimmer — 미세한 흐르는 하이라이트 */}
                    {pct > 0.05 && (
                        <motion.div
                            aria-hidden
                            className="absolute inset-y-0 pointer-events-none rounded-pill"
                            animate={{ x: ["-60%", "160%"] }}
                            transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut", repeatDelay: 1.2 }}
                            style={{
                                left: 0,
                                width: "30%",
                                background: `linear-gradient(90deg, transparent 0%, ${color}44 50%, transparent 100%)`,
                                mixBlendMode: "screen",
                            }}
                        />
                    )}
                </motion.div>
            </div>
        </div>
    );
}
